import { CSSProperties } from 'react';

/**
 * Type for media configuration in onboarding steps.
 */
interface OnboardingMedia {
  type: 'image' | 'video' | 'audio' | 'document';
  source: string;
  caption?: string;
  filename?: string;
  mimeType?: string;
}

/**
 * Type for time picker configuration.
 */
interface TimePickerConfig {
  startHour?: number;
  endHour?: number;
  intervalMinutes?: number;
  format?: '12h' | '24h';
}

/**
 * Type for subscription plan in payment config.
 */
interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency?: string;
  interval: 'trial' | 'weekly' | 'monthly' | 'yearly';
  savingsPercent?: number;
  badge?: string;
  features?: string[];
}

/**
 * Type for payment configuration.
 * Supports external Stripe payment links for regions where in-app payments
 * are not available.
 */
interface PaymentConfig {
  /** Stripe payment link URL for external checkout */
  stripePaymentLink?: string;
  /** Button text for the payment link CTA (default: "Subscribe Now") */
  ctaButtonText?: string;
  /** Optional plans to display for informational purposes */
  plans?: SubscriptionPlan[];
  headerText?: string;
  footerText?: string;
}

/**
 * Type for date picker configuration.
 */
interface DatePickerConfig {
  minDate?: string;
  maxDate?: string;
  format?: string;
  showTime?: boolean;
}

/**
 * Type for multi-select configuration.
 */
interface MultiSelectConfig {
  minSelections?: number;
  maxSelections?: number;
  layout?: 'grid' | 'list' | 'chips';
}

/**
 * Type for onboarding step configuration.
 */
interface OnboardingStep {
  id: string;
  type: 'text' | 'buttons' | 'list' | 'media' | 'time_picker' | 'payment' | 'informational' | 'date-picker' | 'multi-select' | 'dropdown';
  message: string;
  field?: string;
  validation?: {
    type: string;
    pattern?: string;
    min?: number;
    max?: number;
  };
  media?: OnboardingMedia;
  continueButtonText?: string;
  options?: Array<{ id: string; label: string }>;
  timePickerConfig?: TimePickerConfig;
  paymentConfig?: PaymentConfig;
  datePickerConfig?: DatePickerConfig;
  multiSelectConfig?: MultiSelectConfig;
  dropdownConfig?: DropdownConfig;
}

/**
 * Configuration for dropdown steps.
 */
interface DropdownConfig {
  placeholder?: string;
  searchable?: boolean;
}

/**
 * Props for the OnboardingStepPreview component.
 */
interface OnboardingStepPreviewProps {
  step: OnboardingStep | null;
  stepNumber?: number;
  totalSteps?: number;
  isCompletionMessage?: boolean;
  completionMessage?: string;
  platform?: 'chat' | 'desktop';
}

/**
 * Reusable component that simulates a mobile messaging UI or desktop web app UI
 * for previewing onboarding steps.
 * Renders different step types appropriately (text, buttons, lists, media, etc.).
 * When platform is 'desktop', renders in a web app card style instead of WhatsApp chat style.
 */
export function OnboardingStepPreview({
  step,
  stepNumber,
  totalSteps,
  isCompletionMessage = false,
  completionMessage = '',
  platform = 'chat',
}: OnboardingStepPreviewProps) {
  // If platform is desktop, render the desktop preview
  if (platform === 'desktop') {
    return (
      <DesktopPreview
        step={step}
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        isCompletionMessage={isCompletionMessage}
        completionMessage={completionMessage}
      />
    );
  }
  // Render empty state when no step is selected
  if (!step && !isCompletionMessage) {
    return (
      <div style={styles.container}>
        <div style={styles.phoneFrame}>
          <div style={styles.phoneNotch} />
          <div style={styles.phoneHeader}>
            <div style={styles.headerAvatar}>B</div>
            <div style={styles.headerInfo}>
              <div style={styles.headerName}>Bot</div>
              <div style={styles.headerStatus}>Online</div>
            </div>
          </div>
          <div style={styles.chatContainer}>
            <div style={styles.emptyPreview}>
              <div style={styles.emptyIcon}>👆</div>
              <div style={styles.emptyText}>Click on a step to preview</div>
              <div style={styles.emptyHint}>
                Select an onboarding step or the completion message to see how it will appear to users
              </div>
            </div>
          </div>
          <div style={styles.inputArea}>
            <div style={styles.inputField}>Type a message...</div>
            <div style={styles.sendButton}>➤</div>
          </div>
        </div>
      </div>
    );
  }

  // Render completion message preview
  if (isCompletionMessage) {
    return (
      <div style={styles.container}>
        <div style={styles.phoneFrame}>
          <div style={styles.phoneNotch} />
          <div style={styles.phoneHeader}>
            <div style={styles.headerAvatar}>B</div>
            <div style={styles.headerInfo}>
              <div style={styles.headerName}>Bot</div>
              <div style={styles.headerStatus}>Online</div>
            </div>
          </div>
          <div style={styles.chatContainer}>
            <div style={styles.previewLabel}>Completion Message</div>
            <div style={styles.messageBubbleContainer}>
              <div style={styles.botMessageBubble}>
                {completionMessage || (
                  <span style={styles.placeholderText}>
                    No completion message set
                  </span>
                )}
              </div>
              <div style={styles.messageTime}>Just now</div>
            </div>
            <div style={styles.completionIndicator}>
              <div style={styles.completionIcon}>✓</div>
              <div style={styles.completionText}>Onboarding Complete</div>
            </div>
          </div>
          <div style={styles.inputArea}>
            <div style={styles.inputField}>Type a message...</div>
            <div style={styles.sendButton}>➤</div>
          </div>
        </div>
      </div>
    );
  }

  // Render step preview
  return (
    <div style={styles.container}>
      <div style={styles.phoneFrame}>
        <div style={styles.phoneNotch} />
        <div style={styles.phoneHeader}>
          <div style={styles.headerAvatar}>B</div>
          <div style={styles.headerInfo}>
            <div style={styles.headerName}>Bot</div>
            <div style={styles.headerStatus}>Online</div>
          </div>
        </div>
        <div style={styles.chatContainer}>
          {stepNumber && totalSteps && (
            <div style={styles.previewLabel}>
              Step {stepNumber} of {totalSteps}
            </div>
          )}

          {/* Bot message bubble */}
          <div style={styles.messageBubbleContainer}>
            <div style={styles.botMessageBubble}>
              {step?.message || (
                <span style={styles.placeholderText}>
                  No message set
                </span>
              )}
            </div>
            <div style={styles.messageTime}>Just now</div>
          </div>

          {/* Step-type specific UI */}
          {step && renderStepTypeUI(step)}
        </div>

        {/* Input area - changes based on step type */}
        {renderInputArea(step)}
      </div>
    </div>
  );
}

/**
 * Generate time slots based on time picker config.
 */
function generateTimeSlots(config?: TimePickerConfig): string[] {
  const startHour = config?.startHour ?? 6;
  const endHour = config?.endHour ?? 22;
  const intervalMinutes = config?.intervalMinutes ?? 30;
  const format = config?.format ?? '12h';

  const slots: string[] = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      if (hour === endHour && minute > 0) break;

      if (format === '12h') {
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const displayMinute = minute.toString().padStart(2, '0');
        slots.push(`${displayHour}:${displayMinute} ${period}`);
      } else {
        const displayHour = hour.toString().padStart(2, '0');
        const displayMinute = minute.toString().padStart(2, '0');
        slots.push(`${displayHour}:${displayMinute}`);
      }
    }
  }
  return slots;
}

/**
 * Format price with currency symbol.
 */
function formatPrice(price: number, currency?: string): string {
  const curr = currency || 'USD';
  const symbols: Record<string, string> = {
    'USD': '$',
    'EUR': '\u20AC',
    'GBP': '\u00A3',
    'JPY': '\u00A5',
    'INR': '\u20B9',
  };
  const symbol = symbols[curr] || curr + ' ';
  return `${symbol}${price.toFixed(2)}`;
}

/**
 * Format interval for display.
 */
function formatInterval(interval: SubscriptionPlan['interval']): string {
  const map: Record<string, string> = {
    'trial': 'trial',
    'weekly': 'wk',
    'monthly': 'mo',
    'yearly': 'yr',
  };
  return map[interval] || interval;
}

/**
 * Desktop Web App Preview component.
 * Renders onboarding steps in a card/web-style container matching the web app design.
 */
function DesktopPreview({
  step,
  stepNumber,
  totalSteps,
  isCompletionMessage,
  completionMessage,
}: Omit<OnboardingStepPreviewProps, 'platform'>) {
  // Empty state when no step is selected
  if (!step && !isCompletionMessage) {
    return (
      <div style={desktopStyles.container}>
        <div style={desktopStyles.webAppFrame}>
          <div style={desktopStyles.header}>
            <div style={desktopStyles.headerLogo}>
              <div style={desktopStyles.logoIcon}>B</div>
              <span style={desktopStyles.headerTitle}>Onboarding</span>
            </div>
          </div>
          <div style={desktopStyles.content}>
            <div style={desktopStyles.emptyState}>
              <div style={desktopStyles.emptyIcon}>👆</div>
              <div style={desktopStyles.emptyText}>Click on a step to preview</div>
              <div style={desktopStyles.emptyHint}>
                Select an onboarding step to see how it will appear in the web app
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Completion message preview
  if (isCompletionMessage) {
    return (
      <div style={desktopStyles.container}>
        <div style={desktopStyles.webAppFrame}>
          <div style={desktopStyles.header}>
            <div style={desktopStyles.headerLogo}>
              <div style={desktopStyles.logoIcon}>B</div>
              <span style={desktopStyles.headerTitle}>Onboarding</span>
            </div>
            {totalSteps && totalSteps > 0 && (
              <div style={desktopStyles.progressIndicator}>
                <div style={desktopStyles.progressBar}>
                  <div style={{ ...desktopStyles.progressFill, width: '100%' }} />
                </div>
                <span style={desktopStyles.progressText}>Complete</span>
              </div>
            )}
          </div>
          <div style={desktopStyles.content}>
            <div style={desktopStyles.card}>
              <div style={desktopStyles.completionContent}>
                <div style={desktopStyles.completionIcon}>✓</div>
                <h2 style={desktopStyles.completionTitle}>All Done!</h2>
                <p style={desktopStyles.completionMessage}>
                  {completionMessage || <span style={desktopStyles.placeholder}>No completion message set</span>}
                </p>
                <button style={desktopStyles.primaryButton}>Continue</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step preview
  const progressPercentage = stepNumber && totalSteps ? (stepNumber / totalSteps) * 100 : 0;

  return (
    <div style={desktopStyles.container}>
      <div style={desktopStyles.webAppFrame}>
        <div style={desktopStyles.header}>
          <div style={desktopStyles.headerLogo}>
            <div style={desktopStyles.logoIcon}>B</div>
            <span style={desktopStyles.headerTitle}>Onboarding</span>
          </div>
          {totalSteps && totalSteps > 0 && (
            <div style={desktopStyles.progressIndicator}>
              <div style={desktopStyles.progressBar}>
                <div style={{ ...desktopStyles.progressFill, width: `${progressPercentage}%` }} />
              </div>
              <span style={desktopStyles.progressText}>Step {stepNumber} of {totalSteps}</span>
            </div>
          )}
        </div>
        <div style={desktopStyles.content}>
          <div style={desktopStyles.card}>
            <div style={desktopStyles.cardHeader}>
              <h2 style={desktopStyles.cardTitle}>
                {step?.message || <span style={desktopStyles.placeholder}>No message set</span>}
              </h2>
            </div>
            <div style={desktopStyles.cardBody}>
              {step && renderDesktopStepUI(step)}
            </div>
            <div style={desktopStyles.cardFooter}>
              {renderDesktopFooter(step)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Render desktop-specific step UI elements
 */
function renderDesktopStepUI(step: OnboardingStep): JSX.Element | null {
  switch (step.type) {
    case 'dropdown':
      return (
        <div style={desktopStyles.dropdownContainer}>
          <div style={desktopStyles.selectWrapper}>
            <select style={desktopStyles.selectInput} disabled>
              <option>{step.dropdownConfig?.placeholder || 'Select an option...'}</option>
              {step.options?.map((option, idx) => (
                <option key={option.id || idx} value={option.id}>{option.label}</option>
              ))}
            </select>
            <span style={desktopStyles.selectArrow}>▼</span>
          </div>
          {step.dropdownConfig?.searchable && (
            <div style={desktopStyles.searchHint}>
              <span style={desktopStyles.searchIcon}>🔍</span>
              <span>Searchable dropdown</span>
            </div>
          )}
          {step.options && step.options.length > 0 && (
            <div style={desktopStyles.optionPreview}>
              <div style={desktopStyles.optionPreviewLabel}>Available options:</div>
              {step.options.slice(0, 5).map((option, idx) => (
                <div key={option.id || idx} style={desktopStyles.optionPreviewItem}>
                  {option.label}
                </div>
              ))}
              {step.options.length > 5 && (
                <div style={desktopStyles.optionPreviewMore}>
                  +{step.options.length - 5} more options
                </div>
              )}
            </div>
          )}
        </div>
      );

    case 'date-picker':
      return (
        <div style={desktopStyles.datePickerContainer}>
          <div style={desktopStyles.dateInputWrapper}>
            <input
              type="text"
              style={desktopStyles.dateInput}
              placeholder={step.datePickerConfig?.showTime ? 'Select date and time...' : 'Select a date...'}
              readOnly
            />
            <span style={desktopStyles.dateIcon}>📅</span>
          </div>
          <div style={desktopStyles.calendarPreview}>
            <div style={desktopStyles.calendarHeader}>
              <button style={desktopStyles.calendarNavButton}>◀</button>
              <span style={desktopStyles.calendarMonth}>March 2026</span>
              <button style={desktopStyles.calendarNavButton}>▶</button>
            </div>
            <div style={desktopStyles.calendarGrid}>
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                <div key={day} style={desktopStyles.calendarDayHeader}>{day}</div>
              ))}
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map((day) => (
                <div
                  key={day}
                  style={day === 3 ? desktopStyles.calendarDaySelected : desktopStyles.calendarDay}
                >
                  {day}
                </div>
              ))}
            </div>
            {step.datePickerConfig?.showTime && (
              <div style={desktopStyles.timePickerSection}>
                <span style={desktopStyles.timeLabel}>Time:</span>
                <input type="text" style={desktopStyles.timeInput} value="10:00 AM" readOnly />
              </div>
            )}
          </div>
          {step.datePickerConfig?.format && (
            <div style={desktopStyles.formatHint}>
              Format: {step.datePickerConfig.format}
            </div>
          )}
        </div>
      );

    case 'multi-select':
      const layout = step.multiSelectConfig?.layout || 'list';
      const options = step.options && step.options.length > 0
        ? step.options
        : [{ id: '1', label: 'Option 1' }, { id: '2', label: 'Option 2' }, { id: '3', label: 'Option 3' }];

      return (
        <div style={desktopStyles.multiSelectContainer}>
          {step.multiSelectConfig?.minSelections || step.multiSelectConfig?.maxSelections ? (
            <div style={desktopStyles.selectionHint}>
              {step.multiSelectConfig?.minSelections && (
                <span>Min: {step.multiSelectConfig.minSelections}</span>
              )}
              {step.multiSelectConfig?.minSelections && step.multiSelectConfig?.maxSelections && ' | '}
              {step.multiSelectConfig?.maxSelections && (
                <span>Max: {step.multiSelectConfig.maxSelections}</span>
              )}
            </div>
          ) : null}
          <div style={
            layout === 'grid' ? desktopStyles.multiSelectGrid :
            layout === 'chips' ? desktopStyles.multiSelectChips :
            desktopStyles.multiSelectList
          }>
            {options.map((option, idx) => {
              const isSelected = idx === 0;
              if (layout === 'chips') {
                return (
                  <div
                    key={option.id || idx}
                    style={isSelected ? desktopStyles.chipSelected : desktopStyles.chip}
                  >
                    {isSelected && <span style={desktopStyles.chipCheckmark}>✓</span>}
                    {option.label}
                  </div>
                );
              }
              return (
                <div
                  key={option.id || idx}
                  style={isSelected ? desktopStyles.multiSelectItemSelected : desktopStyles.multiSelectItem}
                >
                  <div style={isSelected ? desktopStyles.checkboxSelected : desktopStyles.checkbox}>
                    {isSelected && <span style={desktopStyles.checkmark}>✓</span>}
                  </div>
                  <span style={desktopStyles.multiSelectLabel}>{option.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      );

    case 'buttons':
      return (
        <div style={desktopStyles.buttonsContainer}>
          {step.options && step.options.length > 0 ? (
            step.options.map((option, idx) => (
              <button
                key={option.id || idx}
                style={idx === 0 ? desktopStyles.buttonPrimary : desktopStyles.buttonSecondary}
              >
                {option.label}
              </button>
            ))
          ) : (
            <>
              <button style={desktopStyles.buttonPrimary}>Option 1</button>
              <button style={desktopStyles.buttonSecondary}>Option 2</button>
              <button style={desktopStyles.buttonSecondary}>Option 3</button>
            </>
          )}
        </div>
      );

    case 'list':
      return (
        <div style={desktopStyles.listContainer}>
          {step.options && step.options.length > 0 ? (
            step.options.map((option, idx) => (
              <div
                key={option.id || idx}
                style={idx === 0 ? desktopStyles.listItemSelected : desktopStyles.listItem}
              >
                <div style={idx === 0 ? desktopStyles.radioSelected : desktopStyles.radio}>
                  {idx === 0 && <div style={desktopStyles.radioDot} />}
                </div>
                <span>{option.label}</span>
              </div>
            ))
          ) : (
            <>
              <div style={desktopStyles.listItemSelected}>
                <div style={desktopStyles.radioSelected}><div style={desktopStyles.radioDot} /></div>
                <span>Option 1</span>
              </div>
              <div style={desktopStyles.listItem}>
                <div style={desktopStyles.radio} />
                <span>Option 2</span>
              </div>
              <div style={desktopStyles.listItem}>
                <div style={desktopStyles.radio} />
                <span>Option 3</span>
              </div>
            </>
          )}
        </div>
      );

    case 'media':
      return (
        <div style={desktopStyles.mediaContainer}>
          {step.media?.source ? (
            step.media.type === 'image' ? (
              <img
                src={step.media.source}
                alt="Media preview"
                style={desktopStyles.mediaImage}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : step.media.type === 'video' ? (
              <div style={desktopStyles.mediaPlaceholder}>
                <div style={desktopStyles.mediaIcon}>▶</div>
                <div style={desktopStyles.mediaLabel}>Video</div>
              </div>
            ) : step.media.type === 'audio' ? (
              <div style={desktopStyles.audioPlayer}>
                <button style={desktopStyles.playButton}>▶</button>
                <div style={desktopStyles.audioProgress}>
                  <div style={desktopStyles.audioProgressBar} />
                </div>
                <span style={desktopStyles.audioDuration}>0:00</span>
              </div>
            ) : (
              <div style={desktopStyles.documentPreview}>
                <div style={desktopStyles.documentIcon}>📄</div>
                <div style={desktopStyles.documentInfo}>
                  <div style={desktopStyles.documentName}>{step.media.filename || 'Document'}</div>
                  <div style={desktopStyles.documentAction}>Click to download</div>
                </div>
              </div>
            )
          ) : (
            <div style={desktopStyles.mediaPlaceholder}>
              <div style={desktopStyles.mediaIcon}>🖼</div>
              <div style={desktopStyles.mediaLabel}>Media Preview</div>
              <div style={desktopStyles.mediaHint}>Add a media URL to preview</div>
            </div>
          )}
          {step.media?.caption && (
            <div style={desktopStyles.mediaCaption}>{step.media.caption}</div>
          )}
        </div>
      );

    case 'time_picker':
      return (
        <div style={desktopStyles.timePickerContainer}>
          <div style={desktopStyles.timeSlotsGrid}>
            {generateTimeSlots(step.timePickerConfig).slice(0, 8).map((slot, idx) => (
              <button
                key={idx}
                style={idx === 0 ? desktopStyles.timeSlotSelected : desktopStyles.timeSlot}
              >
                {slot}
              </button>
            ))}
          </div>
          {generateTimeSlots(step.timePickerConfig).length > 8 && (
            <div style={desktopStyles.moreSlots}>
              +{generateTimeSlots(step.timePickerConfig).length - 8} more time slots
            </div>
          )}
        </div>
      );

    case 'payment':
      return (
        <div style={desktopStyles.paymentContainer}>
          <div style={desktopStyles.paymentCard}>
            <div style={desktopStyles.paymentHeader}>
              <span style={desktopStyles.paymentIcon}>💳</span>
              <span style={desktopStyles.paymentTitle}>
                {step.paymentConfig?.headerText || 'Premium Subscription'}
              </span>
            </div>
            {step.paymentConfig?.stripePaymentLink ? (
              <div style={desktopStyles.paymentLinkInfo}>
                <p style={desktopStyles.paymentLinkText}>
                  Click the button below to complete your subscription securely via Stripe.
                </p>
              </div>
            ) : (
              <div style={desktopStyles.paymentLinkPlaceholder}>
                <span style={desktopStyles.placeholder}>Add a Stripe payment link URL</span>
              </div>
            )}
            {step.paymentConfig?.footerText && (
              <div style={desktopStyles.paymentFooter}>{step.paymentConfig.footerText}</div>
            )}
          </div>
          <button style={desktopStyles.paymentButton}>
            <span>↗</span>
            <span>{step.paymentConfig?.ctaButtonText || 'Subscribe Now'}</span>
          </button>
        </div>
      );

    case 'informational':
      return null; // Informational just shows the message, footer has the continue button

    case 'text':
    default:
      return (
        <div style={desktopStyles.textInputContainer}>
          <input
            type="text"
            style={desktopStyles.textInput}
            placeholder="Type your response..."
            readOnly
          />
        </div>
      );
  }
}

/**
 * Render desktop footer with appropriate action button
 */
function renderDesktopFooter(step: OnboardingStep | null): JSX.Element {
  if (!step) {
    return <button style={desktopStyles.primaryButton}>Continue</button>;
  }

  switch (step.type) {
    case 'buttons':
    case 'list':
    case 'payment':
      return <></>; // These have their own action elements
    case 'informational':
      return (
        <button style={desktopStyles.primaryButton}>
          {step.continueButtonText || 'Continue'}
        </button>
      );
    case 'multi-select':
    case 'dropdown':
    case 'date-picker':
    case 'time_picker':
      return <button style={desktopStyles.primaryButton}>Continue</button>;
    case 'text':
    case 'media':
    default:
      return <button style={desktopStyles.primaryButton}>Submit</button>;
  }
}

/**
 * Desktop preview styles - matches web app design system
 */
const desktopStyles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '16px',
  },
  webAppFrame: {
    width: '320px',
    minHeight: '520px',
    backgroundColor: '#f8f6f3',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    backgroundColor: '#ffffff',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  headerLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  logoIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontWeight: '600',
    fontSize: '14px',
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#3D3A36',
  },
  progressIndicator: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  progressBar: {
    height: '4px',
    backgroundColor: 'rgba(93, 86, 78, 0.1)',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#3b82f6',
    borderRadius: '2px',
    transition: 'width 0.3s ease',
  },
  progressText: {
    fontSize: '12px',
    color: '#5C564E',
  },
  content: {
    flex: 1,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  cardHeader: {
    padding: '20px 20px 0 20px',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#3D3A36',
    lineHeight: '1.4',
    margin: 0,
  },
  cardBody: {
    padding: '16px 20px',
    flex: 1,
  },
  cardFooter: {
    padding: '0 20px 20px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  placeholder: {
    color: '#9B9285',
    fontStyle: 'italic',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    padding: '40px 20px',
  },
  emptyIcon: {
    fontSize: '40px',
    marginBottom: '16px',
  },
  emptyText: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#3D3A36',
    marginBottom: '8px',
  },
  emptyHint: {
    fontSize: '13px',
    color: '#9B9285',
    maxWidth: '200px',
  },
  completionContent: {
    padding: '32px 20px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  completionIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: '#5A8A5E',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    marginBottom: '16px',
  },
  completionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#3D3A36',
    marginBottom: '8px',
    margin: '0 0 8px 0',
  },
  completionMessage: {
    fontSize: '14px',
    color: '#5C564E',
    lineHeight: '1.5',
    marginBottom: '20px',
    margin: '0 0 20px 0',
  },
  primaryButton: {
    width: '100%',
    padding: '12px 20px',
    backgroundColor: '#3D3A36',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },

  // Dropdown styles
  dropdownContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  selectWrapper: {
    position: 'relative',
  },
  selectInput: {
    width: '100%',
    padding: '12px 36px 12px 12px',
    backgroundColor: '#ffffff',
    border: '1px solid rgba(93, 86, 78, 0.2)',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#3D3A36',
    appearance: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  selectArrow: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '10px',
    color: '#9B9285',
    pointerEvents: 'none',
  },
  searchHint: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#5C564E',
    backgroundColor: 'rgba(93, 86, 78, 0.06)',
    padding: '6px 10px',
    borderRadius: '6px',
  },
  searchIcon: {
    fontSize: '12px',
  },
  optionPreview: {
    backgroundColor: 'rgba(93, 86, 78, 0.04)',
    borderRadius: '8px',
    padding: '12px',
  },
  optionPreviewLabel: {
    fontSize: '11px',
    fontWeight: '500',
    color: '#9B9285',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
  optionPreviewItem: {
    fontSize: '13px',
    color: '#5C564E',
    padding: '6px 0',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
  },
  optionPreviewMore: {
    fontSize: '12px',
    color: '#9B9285',
    marginTop: '8px',
    fontStyle: 'italic',
  },

  // Date picker styles
  datePickerContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  dateInputWrapper: {
    position: 'relative',
  },
  dateInput: {
    width: '100%',
    padding: '12px 36px 12px 12px',
    backgroundColor: '#ffffff',
    border: '1px solid rgba(93, 86, 78, 0.2)',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#3D3A36',
    boxSizing: 'border-box',
  },
  dateIcon: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '16px',
  },
  calendarPreview: {
    backgroundColor: '#ffffff',
    border: '1px solid rgba(93, 86, 78, 0.1)',
    borderRadius: '8px',
    padding: '12px',
  },
  calendarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  calendarNavButton: {
    width: '28px',
    height: '28px',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#5C564E',
    cursor: 'pointer',
    borderRadius: '4px',
    fontSize: '12px',
  },
  calendarMonth: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#3D3A36',
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px',
  },
  calendarDayHeader: {
    textAlign: 'center',
    fontSize: '11px',
    fontWeight: '500',
    color: '#9B9285',
    padding: '4px',
  },
  calendarDay: {
    textAlign: 'center',
    fontSize: '13px',
    color: '#3D3A36',
    padding: '6px',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  calendarDaySelected: {
    textAlign: 'center',
    fontSize: '13px',
    color: '#ffffff',
    backgroundColor: '#3b82f6',
    padding: '6px',
    borderRadius: '4px',
    fontWeight: '500',
  },
  timePickerSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid rgba(93, 86, 78, 0.1)',
  },
  timeLabel: {
    fontSize: '13px',
    color: '#5C564E',
    fontWeight: '500',
  },
  timeInput: {
    flex: 1,
    padding: '8px 12px',
    border: '1px solid rgba(93, 86, 78, 0.2)',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#3D3A36',
    textAlign: 'center',
  },
  formatHint: {
    fontSize: '12px',
    color: '#9B9285',
    fontStyle: 'italic',
  },

  // Multi-select styles
  multiSelectContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  selectionHint: {
    fontSize: '12px',
    color: '#5C564E',
    backgroundColor: 'rgba(93, 86, 78, 0.06)',
    padding: '6px 10px',
    borderRadius: '6px',
  },
  multiSelectList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  multiSelectGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px',
  },
  multiSelectChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  multiSelectItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    backgroundColor: '#ffffff',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  multiSelectItemSelected: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
    border: '1px solid #3b82f6',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    border: '2px solid rgba(93, 86, 78, 0.3)',
    flexShrink: 0,
  },
  checkboxSelected: {
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    backgroundColor: '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  checkmark: {
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: 'bold',
  },
  multiSelectLabel: {
    fontSize: '14px',
    color: '#3D3A36',
  },
  chip: {
    padding: '8px 14px',
    backgroundColor: '#ffffff',
    border: '1px solid rgba(93, 86, 78, 0.2)',
    borderRadius: '20px',
    fontSize: '13px',
    color: '#3D3A36',
    cursor: 'pointer',
  },
  chipSelected: {
    padding: '8px 14px',
    backgroundColor: '#3b82f6',
    border: '1px solid #3b82f6',
    borderRadius: '20px',
    fontSize: '13px',
    color: '#ffffff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  chipCheckmark: {
    fontSize: '10px',
  },

  // Buttons styles
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  buttonPrimary: {
    width: '100%',
    padding: '12px 20px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  buttonSecondary: {
    width: '100%',
    padding: '12px 20px',
    backgroundColor: '#ffffff',
    color: '#3D3A36',
    border: '1px solid rgba(93, 86, 78, 0.2)',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },

  // List styles
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#ffffff',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  listItemSelected: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
    border: '1px solid #3b82f6',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  radio: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '2px solid rgba(93, 86, 78, 0.3)',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioSelected: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '2px solid #3b82f6',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
  },

  // Media styles
  mediaContainer: {
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    border: '1px solid rgba(93, 86, 78, 0.1)',
  },
  mediaImage: {
    width: '100%',
    maxHeight: '180px',
    objectFit: 'cover',
    display: 'block',
  },
  mediaPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 16px',
    backgroundColor: 'rgba(93, 86, 78, 0.04)',
  },
  mediaIcon: {
    fontSize: '32px',
    marginBottom: '8px',
  },
  mediaLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#3D3A36',
  },
  mediaHint: {
    fontSize: '12px',
    color: '#9B9285',
    marginTop: '4px',
  },
  mediaCaption: {
    padding: '12px',
    fontSize: '13px',
    color: '#5C564E',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
  },
  audioPlayer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
  },
  playButton: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
  },
  audioProgress: {
    flex: 1,
    height: '4px',
    backgroundColor: 'rgba(93, 86, 78, 0.1)',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  audioProgressBar: {
    width: '30%',
    height: '100%',
    backgroundColor: '#3b82f6',
  },
  audioDuration: {
    fontSize: '12px',
    color: '#9B9285',
  },
  documentPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
  },
  documentIcon: {
    fontSize: '32px',
  },
  documentInfo: {
    flex: 1,
  },
  documentName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#3D3A36',
  },
  documentAction: {
    fontSize: '12px',
    color: '#3b82f6',
    marginTop: '2px',
  },

  // Time picker styles
  timePickerContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  timeSlotsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px',
  },
  timeSlot: {
    padding: '10px',
    backgroundColor: '#ffffff',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#3D3A36',
    cursor: 'pointer',
    textAlign: 'center',
  },
  timeSlotSelected: {
    padding: '10px',
    backgroundColor: '#3b82f6',
    border: '1px solid #3b82f6',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#ffffff',
    cursor: 'pointer',
    textAlign: 'center',
    fontWeight: '500',
  },
  moreSlots: {
    fontSize: '12px',
    color: '#9B9285',
    textAlign: 'center',
    fontStyle: 'italic',
  },

  // Payment styles
  paymentContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  paymentCard: {
    backgroundColor: '#ffffff',
    border: '1px solid rgba(93, 86, 78, 0.1)',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  paymentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: 'rgba(93, 86, 78, 0.04)',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
  },
  paymentIcon: {
    fontSize: '18px',
  },
  paymentTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#3D3A36',
  },
  paymentLinkInfo: {
    padding: '16px',
  },
  paymentLinkText: {
    fontSize: '13px',
    color: '#5C564E',
    lineHeight: '1.5',
    margin: 0,
  },
  paymentLinkPlaceholder: {
    padding: '16px',
    textAlign: 'center',
  },
  paymentFooter: {
    padding: '12px 16px',
    fontSize: '11px',
    color: '#9B9285',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
    textAlign: 'center',
  },
  paymentButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },

  // Text input styles
  textInputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  textInput: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#ffffff',
    border: '1px solid rgba(93, 86, 78, 0.2)',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#3D3A36',
    boxSizing: 'border-box',
  },
};

/**
 * Render step-type specific UI elements
 */
function renderStepTypeUI(step: OnboardingStep): JSX.Element | null {
  switch (step.type) {
    case 'buttons':
      return (
        <div style={styles.buttonsContainer}>
          {step.options && step.options.length > 0 ? (
            step.options.map((option, idx) => (
              <div key={option.id || idx} style={styles.optionButton}>
                {option.label}
              </div>
            ))
          ) : (
            <>
              <div style={styles.optionButton}>Option 1</div>
              <div style={styles.optionButton}>Option 2</div>
              <div style={styles.optionButton}>Option 3</div>
            </>
          )}
        </div>
      );

    case 'list':
      return (
        <div style={styles.listContainer}>
          <div style={styles.listHeader}>Select an option</div>
          {step.options && step.options.length > 0 ? (
            step.options.map((option, idx) => (
              <div key={option.id || idx} style={styles.listItem}>
                <div style={styles.listItemRadio} />
                <div style={styles.listItemText}>{option.label}</div>
              </div>
            ))
          ) : (
            <>
              <div style={styles.listItem}>
                <div style={styles.listItemRadio} />
                <div style={styles.listItemText}>Option 1</div>
              </div>
              <div style={styles.listItem}>
                <div style={styles.listItemRadio} />
                <div style={styles.listItemText}>Option 2</div>
              </div>
              <div style={styles.listItem}>
                <div style={styles.listItemRadio} />
                <div style={styles.listItemText}>Option 3</div>
              </div>
            </>
          )}
          <div style={styles.listSubmit}>Submit</div>
        </div>
      );

    case 'media':
      return (
        <div style={styles.mediaContainer}>
          {step.media?.source ? (
            step.media.type === 'image' ? (
              <img
                src={step.media.source}
                alt="Media preview"
                style={styles.mediaImage}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).nextElementSibling?.removeAttribute('style');
                }}
              />
            ) : step.media.type === 'video' ? (
              <div style={styles.mediaPlaceholder}>
                <div style={styles.mediaIcon}>▶</div>
                <div style={styles.mediaLabel}>Video</div>
              </div>
            ) : step.media.type === 'audio' ? (
              <div style={styles.audioPlaceholder}>
                <div style={styles.audioIcon}>🎵</div>
                <div style={styles.audioWaveform}>
                  <div style={styles.audioBar} />
                  <div style={{...styles.audioBar, height: '16px'}} />
                  <div style={{...styles.audioBar, height: '12px'}} />
                  <div style={{...styles.audioBar, height: '20px'}} />
                  <div style={{...styles.audioBar, height: '8px'}} />
                  <div style={{...styles.audioBar, height: '14px'}} />
                </div>
                <div style={styles.audioDuration}>0:00</div>
              </div>
            ) : (
              <div style={styles.documentPlaceholder}>
                <div style={styles.documentIcon}>📄</div>
                <div style={styles.documentName}>{step.media.filename || 'Document'}</div>
              </div>
            )
          ) : (
            <div style={styles.mediaPlaceholder}>
              <div style={styles.mediaIcon}>🖼</div>
              <div style={styles.mediaLabel}>Media Preview</div>
              <div style={styles.mediaHint}>Add a media URL to preview</div>
            </div>
          )}
          {step.media?.caption && (
            <div style={styles.mediaCaption}>{step.media.caption}</div>
          )}
        </div>
      );

    case 'time_picker':
      return (
        <div style={styles.timePickerContainer}>
          <div style={styles.timePickerHeader}>Select a time</div>
          <div style={styles.timePickerGrid}>
            {generateTimeSlots(step.timePickerConfig).slice(0, 6).map((slot, idx) => (
              <div key={idx} style={styles.timeSlot}>{slot}</div>
            ))}
          </div>
          {generateTimeSlots(step.timePickerConfig).length > 6 && (
            <div style={styles.timePickerMore}>
              +{generateTimeSlots(step.timePickerConfig).length - 6} more slots
            </div>
          )}
        </div>
      );

    case 'payment':
      const headerText = step.paymentConfig?.headerText || 'Premium Subscription';
      const ctaButtonText = step.paymentConfig?.ctaButtonText || 'Subscribe Now';
      const stripePaymentLink = step.paymentConfig?.stripePaymentLink;
      return (
        <div style={styles.paymentMessageContainer}>
          {/* WhatsApp-style payment CTA message */}
          <div style={styles.paymentBubble}>
            <div style={styles.paymentBubbleHeader}>
              <span style={styles.paymentIcon}>💳</span>
              <span style={styles.paymentTitle}>{headerText}</span>
            </div>
            <div style={styles.paymentDivider} />
            <div style={styles.paymentLinkInfo}>
              <div style={styles.paymentLinkText}>
                {stripePaymentLink ? (
                  <>Tap the button below to complete your subscription securely via Stripe.</>
                ) : (
                  <span style={styles.placeholderText}>Add a Stripe payment link URL in the editor</span>
                )}
              </div>
            </div>
            {step.paymentConfig?.footerText && (
              <div style={styles.paymentBubbleFooter}>{step.paymentConfig.footerText}</div>
            )}
          </div>
          {/* WhatsApp CTA button for Stripe payment link */}
          <div style={styles.ctaButtonContainer}>
            <div style={styles.ctaButton}>
              <span style={styles.ctaButtonIcon}>↗</span>
              <span>{ctaButtonText}</span>
            </div>
          </div>
        </div>
      );

    case 'informational':
      return (
        <div style={styles.informationalContainer}>
          {/* WhatsApp CTA-style continue button */}
          <div style={styles.ctaButtonContainer}>
            <div style={styles.ctaButton}>
              <span style={styles.ctaButtonIcon}>↗</span>
              <span>{step.continueButtonText || 'Continue'}</span>
            </div>
          </div>
        </div>
      );

    case 'date-picker':
      return (
        <div style={styles.datePickerContainer}>
          <div style={styles.datePickerHeader}>Select a date</div>
          <div style={styles.datePickerInput}>
            <span style={styles.datePickerIcon}>📅</span>
            <span style={styles.datePickerPlaceholder}>
              {step.datePickerConfig?.showTime ? 'Select date and time...' : 'Select a date...'}
            </span>
          </div>
          <div style={styles.datePickerCalendarPreview}>
            <div style={styles.calendarRow}>
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                <div key={day} style={styles.calendarDayHeader}>{day}</div>
              ))}
            </div>
            <div style={styles.calendarRow}>
              {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                <div key={day} style={day === 3 ? styles.calendarDaySelected : styles.calendarDay}>{day}</div>
              ))}
            </div>
            <div style={styles.calendarRow}>
              {[8, 9, 10, 11, 12, 13, 14].map((day) => (
                <div key={day} style={styles.calendarDay}>{day}</div>
              ))}
            </div>
          </div>
        </div>
      );

    case 'multi-select':
      const layout = step.multiSelectConfig?.layout || 'list';
      const options = step.options && step.options.length > 0
        ? step.options
        : [{ id: '1', label: 'Option 1' }, { id: '2', label: 'Option 2' }, { id: '3', label: 'Option 3' }];

      return (
        <div style={styles.multiSelectContainer}>
          <div style={styles.multiSelectHeader}>
            Select multiple options
            {step.multiSelectConfig?.minSelections && (
              <span style={styles.multiSelectHint}> (min: {step.multiSelectConfig.minSelections})</span>
            )}
            {step.multiSelectConfig?.maxSelections && (
              <span style={styles.multiSelectHint}> (max: {step.multiSelectConfig.maxSelections})</span>
            )}
          </div>
          <div style={layout === 'grid' ? styles.multiSelectGrid : layout === 'chips' ? styles.multiSelectChips : styles.multiSelectList}>
            {options.map((option, idx) => (
              <div
                key={option.id || idx}
                style={
                  layout === 'chips'
                    ? (idx === 0 ? styles.multiSelectChipSelected : styles.multiSelectChip)
                    : (idx === 0 ? styles.multiSelectItemSelected : styles.multiSelectItem)
                }
              >
                {layout !== 'chips' && (
                  <div style={idx === 0 ? styles.multiSelectCheckboxSelected : styles.multiSelectCheckbox}>
                    {idx === 0 && <span style={styles.multiSelectCheckmark}>✓</span>}
                  </div>
                )}
                <span style={layout === 'chips' && idx === 0 ? styles.multiSelectChipTextSelected : undefined}>
                  {option.label}
                </span>
              </div>
            ))}
          </div>
          <div style={styles.multiSelectSubmit}>Continue</div>
        </div>
      );

    case 'text':
    default:
      return null;
  }
}

/**
 * Render the input area based on step type
 */
function renderInputArea(step: OnboardingStep | null): JSX.Element {
  if (!step) {
    return (
      <div style={styles.inputArea}>
        <div style={styles.inputField}>Type a message...</div>
        <div style={styles.sendButton}>➤</div>
      </div>
    );
  }

  // For button, list, time_picker, payment, informational, date-picker, multi-select - no text input
  if (['buttons', 'list', 'time_picker', 'payment', 'informational', 'date-picker', 'multi-select'].includes(step.type)) {
    return (
      <div style={styles.inputAreaDisabled}>
        <div style={styles.inputFieldDisabled}>
          {step.type === 'buttons' && 'Select a button above'}
          {step.type === 'list' && 'Select from the list above'}
          {step.type === 'time_picker' && 'Select a time above'}
          {step.type === 'payment' && 'Select a plan above'}
          {step.type === 'informational' && 'Tap continue above'}
          {step.type === 'date-picker' && 'Select a date above'}
          {step.type === 'multi-select' && 'Select options above'}
        </div>
      </div>
    );
  }

  // For text and media types - show text input
  return (
    <div style={styles.inputArea}>
      <div style={styles.inputField}>Type your response...</div>
      <div style={styles.sendButton}>➤</div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '20px',
  },
  phoneFrame: {
    width: '320px',
    height: '580px',
    backgroundColor: '#ffffff',
    borderRadius: '36px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  phoneNotch: {
    position: 'absolute',
    top: '0',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '120px',
    height: '24px',
    backgroundColor: '#000000',
    borderBottomLeftRadius: '16px',
    borderBottomRightRadius: '16px',
    zIndex: 10,
  },
  phoneHeader: {
    backgroundColor: '#075e54',
    padding: '44px 16px 12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#128c7e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontWeight: '600',
    fontSize: '16px',
  },
  headerInfo: {
    flex: 1,
  },
  headerName: {
    color: '#ffffff',
    fontWeight: '600',
    fontSize: '16px',
  },
  headerStatus: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '12px',
  },
  chatContainer: {
    flex: 1,
    backgroundColor: '#ece5dd',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23d4cdc4\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    padding: '16px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  previewLabel: {
    textAlign: 'center',
    fontSize: '11px',
    color: '#667781',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: '4px 12px',
    borderRadius: '8px',
    alignSelf: 'center',
    marginBottom: '8px',
  },
  messageBubbleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    maxWidth: '85%',
  },
  botMessageBubble: {
    backgroundColor: '#ffffff',
    padding: '8px 12px',
    borderRadius: '8px',
    borderTopLeftRadius: '0',
    fontSize: '14px',
    lineHeight: '1.4',
    color: '#303030',
    boxShadow: '0 1px 0.5px rgba(0, 0, 0, 0.13)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  messageTime: {
    fontSize: '11px',
    color: '#667781',
    marginTop: '2px',
    marginLeft: '4px',
  },
  placeholderText: {
    color: '#94a3b8',
    fontStyle: 'italic',
  },
  emptyPreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    padding: '20px',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyText: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#475569',
    marginBottom: '8px',
  },
  emptyHint: {
    fontSize: '13px',
    color: '#94a3b8',
    maxWidth: '200px',
  },
  completionIndicator: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '20px',
    padding: '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '12px',
  },
  completionIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#25d366',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    marginBottom: '8px',
  },
  completionText: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#128c7e',
  },
  inputArea: {
    backgroundColor: '#f0f2f5',
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  inputAreaDisabled: {
    backgroundColor: '#f0f2f5',
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputField: {
    flex: 1,
    backgroundColor: '#ffffff',
    padding: '10px 16px',
    borderRadius: '24px',
    fontSize: '14px',
    color: '#667781',
  },
  inputFieldDisabled: {
    backgroundColor: 'transparent',
    padding: '10px 16px',
    fontSize: '13px',
    color: '#94a3b8',
    textAlign: 'center',
  },
  sendButton: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#25d366',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
  },

  // Buttons step type
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '8px',
    width: '100%',
  },
  optionButton: {
    backgroundColor: '#ffffff',
    border: '1px solid #25d366',
    color: '#25d366',
    padding: '10px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'center',
    cursor: 'pointer',
  },

  // List step type
  listContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    marginTop: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 0.5px rgba(0, 0, 0, 0.13)',
  },
  listHeader: {
    padding: '12px 16px',
    backgroundColor: '#075e54',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #e9edef',
    gap: '12px',
  },
  listItemRadio: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid #25d366',
    flexShrink: 0,
  },
  listItemText: {
    fontSize: '14px',
    color: '#303030',
  },
  listSubmit: {
    padding: '12px 16px',
    backgroundColor: '#25d366',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'center',
  },

  // Media step type
  mediaContainer: {
    marginTop: '8px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 0.5px rgba(0, 0, 0, 0.13)',
  },
  mediaImage: {
    width: '100%',
    maxHeight: '200px',
    objectFit: 'cover',
    display: 'block',
  },
  mediaPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 16px',
    backgroundColor: '#f0f2f5',
  },
  mediaIcon: {
    fontSize: '36px',
    marginBottom: '8px',
  },
  mediaLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#475569',
  },
  mediaHint: {
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '4px',
  },
  mediaCaption: {
    padding: '8px 12px',
    fontSize: '13px',
    color: '#303030',
  },
  audioPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    gap: '12px',
  },
  audioIcon: {
    fontSize: '24px',
  },
  audioWaveform: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    flex: 1,
  },
  audioBar: {
    width: '3px',
    height: '10px',
    backgroundColor: '#25d366',
    borderRadius: '2px',
  },
  audioDuration: {
    fontSize: '12px',
    color: '#667781',
  },
  documentPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    gap: '12px',
  },
  documentIcon: {
    fontSize: '32px',
  },
  documentName: {
    fontSize: '14px',
    color: '#303030',
    fontWeight: '500',
  },

  // Time picker step type
  timePickerContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    marginTop: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 0.5px rgba(0, 0, 0, 0.13)',
  },
  timePickerHeader: {
    padding: '12px 16px',
    backgroundColor: '#075e54',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
  },
  timePickerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px',
    padding: '12px',
  },
  timeSlot: {
    padding: '10px',
    backgroundColor: '#f0f2f5',
    borderRadius: '6px',
    fontSize: '13px',
    textAlign: 'center',
    color: '#303030',
  },
  timePickerMore: {
    padding: '8px',
    textAlign: 'center',
    fontSize: '12px',
    color: '#667781',
    borderTop: '1px solid #e9edef',
  },

  // Payment step type - WhatsApp Flows/order style
  paymentMessageContainer: {
    marginTop: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  paymentBubble: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    borderTopLeftRadius: '0',
    boxShadow: '0 1px 0.5px rgba(0, 0, 0, 0.13)',
    overflow: 'hidden',
  },
  paymentBubbleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#f8f9fa',
  },
  paymentIcon: {
    fontSize: '18px',
  },
  paymentTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#303030',
  },
  paymentDivider: {
    height: '1px',
    backgroundColor: '#e9edef',
  },
  paymentPlansList: {
    padding: '4px 0',
  },
  paymentPlanRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    borderBottom: '1px solid #f0f2f5',
  },
  planRowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  planRowRadio: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '2px solid #128c7e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  planRowRadioSelected: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#128c7e',
  },
  planRowInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  planRowName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#303030',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  planRowBadge: {
    backgroundColor: '#25d366',
    color: '#ffffff',
    fontSize: '9px',
    padding: '2px 6px',
    borderRadius: '8px',
    fontWeight: '500',
    textTransform: 'uppercase',
  },
  planRowDetails: {
    fontSize: '12px',
    color: '#667781',
    marginTop: '2px',
  },
  planRowSavings: {
    color: '#25d366',
    fontWeight: '500',
  },
  planRowPrice: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#128c7e',
    textAlign: 'right',
  },
  planRowInterval: {
    fontSize: '12px',
    fontWeight: '400',
    color: '#667781',
  },
  paymentBubbleFooter: {
    padding: '8px 12px',
    fontSize: '11px',
    color: '#667781',
    borderTop: '1px solid #e9edef',
    textAlign: 'center',
  },
  // Payment link info section for Stripe payment links
  paymentLinkInfo: {
    padding: '12px',
  },
  paymentLinkText: {
    fontSize: '13px',
    color: '#303030',
    lineHeight: '1.4',
    textAlign: 'center',
  },

  // WhatsApp CTA button style (shared between payment and informational)
  ctaButtonContainer: {
    marginTop: '4px',
  },
  ctaButton: {
    backgroundColor: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#00a5f4',
    boxShadow: '0 1px 0.5px rgba(0, 0, 0, 0.13)',
    cursor: 'pointer',
  },
  ctaButtonIcon: {
    fontSize: '16px',
  },

  // Informational step type
  informationalContainer: {
    marginTop: '8px',
  },

  // Date picker step type
  datePickerContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    marginTop: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 0.5px rgba(0, 0, 0, 0.13)',
  },
  datePickerHeader: {
    padding: '12px 16px',
    backgroundColor: '#075e54',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
  },
  datePickerInput: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    borderBottom: '1px solid #e9edef',
  },
  datePickerIcon: {
    fontSize: '20px',
  },
  datePickerPlaceholder: {
    fontSize: '14px',
    color: '#667781',
  },
  datePickerCalendarPreview: {
    padding: '12px',
  },
  calendarRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  calendarDayHeader: {
    width: '28px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: '500',
    color: '#667781',
  },
  calendarDay: {
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    color: '#303030',
    borderRadius: '50%',
  },
  calendarDaySelected: {
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    color: '#ffffff',
    backgroundColor: '#25d366',
    borderRadius: '50%',
  },

  // Multi-select step type
  multiSelectContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    marginTop: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 0.5px rgba(0, 0, 0, 0.13)',
  },
  multiSelectHeader: {
    padding: '12px 16px',
    backgroundColor: '#075e54',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
  },
  multiSelectHint: {
    fontWeight: '400',
    opacity: 0.8,
    fontSize: '12px',
  },
  multiSelectList: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 0',
  },
  multiSelectGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px',
    padding: '12px',
  },
  multiSelectChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '12px',
  },
  multiSelectItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    gap: '12px',
    borderBottom: '1px solid #e9edef',
  },
  multiSelectItemSelected: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    gap: '12px',
    borderBottom: '1px solid #e9edef',
    backgroundColor: 'rgba(37, 211, 102, 0.1)',
  },
  multiSelectCheckbox: {
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    border: '2px solid #25d366',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  multiSelectCheckboxSelected: {
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    backgroundColor: '#25d366',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  multiSelectCheckmark: {
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  multiSelectChip: {
    padding: '8px 16px',
    borderRadius: '20px',
    border: '1px solid #25d366',
    fontSize: '13px',
    color: '#303030',
    backgroundColor: '#ffffff',
  },
  multiSelectChipSelected: {
    padding: '8px 16px',
    borderRadius: '20px',
    border: '1px solid #25d366',
    fontSize: '13px',
    backgroundColor: '#25d366',
    color: '#ffffff',
  },
  multiSelectChipTextSelected: {
    color: '#ffffff',
  },
  multiSelectSubmit: {
    padding: '12px 16px',
    backgroundColor: '#25d366',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'center',
  },
};

export default OnboardingStepPreview;
